import { Component, OnInit, OnDestroy, ViewChild, ChangeDetectionStrategy } from '@angular/core';

import { ReactiveFormsModule, FormBuilder, Validators, FormGroup } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { CompanyService, Company } from '../../../services/company.service';
import { ImageCropData } from '../../../services/voucher-product.service';
import { Subject, takeUntil, filter } from 'rxjs';
import { SaveButtonComponent } from '../../save-button/save-button.component';
import { LoggerService } from '../../../services/logger.service';
import { ImageCropperModalComponent } from '../../image-cropper-modal/image-cropper-modal.component';
import { takeSelectedFile, validateImageFile } from '../../../utils/image-file.utils';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { lucideCheck, lucideImagePlus, lucideTriangleAlert, lucideX } from '@ng-icons/lucide';
import { HlmAlertImports } from '@spartan-ng/helm/alert';
import { HlmBadgeImports } from '@spartan-ng/helm/badge';
import { HlmButtonImports } from '@spartan-ng/helm/button';
import { HlmCardImports } from '@spartan-ng/helm/card';
import { HlmFieldImports } from '@spartan-ng/helm/field';
import { HlmInputGroupImports } from '@spartan-ng/helm/input-group';
import { HlmItemImports } from '@spartan-ng/helm/item';
import { HlmSpinnerImports } from '@spartan-ng/helm/spinner';
import { HlmTooltipImports } from '@spartan-ng/helm/tooltip';
import { HlmTypographyImports } from '@spartan-ng/helm/typography';

@Component({
  selector: 'app-branding-settings',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    RouterModule,
    SaveButtonComponent,
    ImageCropperModalComponent,
    NgIcon,
    HlmAlertImports,
    HlmBadgeImports,
    HlmButtonImports,
    HlmCardImports,
    HlmFieldImports,
    HlmInputGroupImports,
    HlmItemImports,
    HlmSpinnerImports,
    HlmTooltipImports,
    HlmTypographyImports,
  ],
  providers: [
    provideIcons({
      lucideCheck,
      lucideImagePlus,
      lucideTriangleAlert,
      lucideX,
    }),
  ],
  templateUrl: './branding-settings.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
})
/**
 * Logo, banner and colours: the company's branding. The storefront, the voucher app
 * and the voucher designs (logo and banner elements, the colour of the bundled
 * artworks) all read them from here.
 */
export class BrandingSettingsComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  private initialFormValues: any;
  loading = false;
  formChanged = false;
  company: Company | null = null;

  @ViewChild('designSaveButton') designSaveButton!: SaveButtonComponent;

  brandingForm: FormGroup;

  // Image upload
  logoFile: File | null = null;
  bannerFile: File | null = null;
  logoPreview: string | null = null;
  bannerPreview: string | null = null;
  logoUploadLoading = false;
  bannerUploadLoading = false;
  currentLogoPath: string | null = null;
  currentBannerPath: string | null = null;
  logoError: string | null = null;
  bannerError: string | null = null;

  // Logo crop modal
  showLogoCropper = false;
  logoFileForCropping: File | null = null;

  // Banner crop modal (595:150 pt = PDF banner slot)
  readonly bannerAspectRatio = 595 / 150;
  showBannerCropper = false;
  bannerFileForCropping: File | null = null;

  // Delete confirmations
  confirmDeleteLogo = false;
  confirmDeleteBanner = false;

  constructor(
    private companyService: CompanyService,
    private fb: FormBuilder,
    private logger: LoggerService,
  ) {
    this.brandingForm = this.fb.group({
      voucher_app_primary_color: [
        '',
        [Validators.required, Validators.pattern(/^#[0-9A-Fa-f]{6}$/)],
      ],
      voucher_app_text_color: ['', [Validators.required, Validators.pattern(/^#[0-9A-Fa-f]{6}$/)]],
    });

    this.brandingForm.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.checkFormChanged());
  }

  ngOnInit() {
    this.companyService.company$
      .pipe(
        takeUntil(this.destroy$),
        filter((c) => c !== null),
      )
      .subscribe((company) => {
        this.company = company;
        this.populateForm(company);
      });
  }

  private checkFormChanged() {
    if (this.initialFormValues) {
      this.formChanged = !Object.keys(this.initialFormValues).every(
        (key) => this.initialFormValues[key] === this.brandingForm.get(key)?.value,
      );
    }
  }

  private populateForm(company: Company) {
    this.brandingForm.patchValue(
      {
        voucher_app_primary_color: company.voucher_app_primary_color || '#000000',
        voucher_app_text_color: company.voucher_app_text_color || '#FFFFFF',
      },
      { emitEvent: false },
    );
    this.currentLogoPath = company.logo_image_path || null;
    this.currentBannerPath = company.banner_image_path || null;
    this.initialFormValues = this.brandingForm.value;
    this.formChanged = false;
  }

  onSubmit(saveButton: SaveButtonComponent) {
    if (!this.brandingForm.valid) return;
    this.loading = true;
    saveButton.hideMessages();
    this.companyService.updateCompanyDetails(this.brandingForm.value).subscribe({
      next: () => {
        this.loading = false;
        this.initialFormValues = this.brandingForm.value;
        this.formChanged = false;
        saveButton.showSuccessMessage();
      },
      error: (error) => {
        const errorMessage =
          error.error?.detail || 'Fehler beim Speichern. Bitte versuche es erneut.';
        this.logger.error('Error saving branding:', error);
        this.loading = false;
        saveButton.showErrorMessage(errorMessage);
      },
    });
  }

  isInvalid(controlName: string): boolean {
    const control = this.brandingForm.get(controlName);
    return !!control?.invalid && !!control?.touched;
  }

  onColorPickerChange(colorField: string, event: Event) {
    const target = event.target as HTMLInputElement;
    this.brandingForm.get(colorField)?.setValue(target.value);
  }

  onColorTextChange(colorField: string, event: Event) {
    const target = event.target as HTMLInputElement;
    this.brandingForm.get(colorField)?.setValue(target.value);
  }

  // Image upload
  onLogoFileSelected(event: Event) {
    // takeSelectedFile also clears the input, so picking the same photo twice
    // in a row still opens the cropper.
    const file = takeSelectedFile(event);
    if (!file) return;

    const problem = validateImageFile(file);
    if (problem) {
      this.logoError = problem;
      return;
    }

    this.logoError = null;
    // Open crop modal instead of direct preview
    this.logoFileForCropping = file;
    this.showLogoCropper = true;
  }

  onLogoCropComplete(event: { file: File; croppedFile: File; cropData: ImageCropData }) {
    this.showLogoCropper = false;
    this.logoFileForCropping = null;
    this.logoError = null;
    this.logoFile = event.croppedFile;
    // Show preview of the cropped result
    const reader = new FileReader();
    reader.onload = (e: any) => {
      this.logoPreview = e.target.result;
    };
    reader.readAsDataURL(event.croppedFile);
  }

  onLogoCropCancel() {
    this.showLogoCropper = false;
    this.logoFileForCropping = null;
  }

  onBannerFileSelected(event: Event) {
    const file = takeSelectedFile(event);
    if (!file) return;

    const problem = validateImageFile(file);
    if (problem) {
      this.bannerError = problem;
      return;
    }

    this.bannerError = null;
    this.bannerFileForCropping = file;
    this.showBannerCropper = true;
  }

  onBannerCropComplete(event: { file: File; croppedFile: File; cropData: ImageCropData }) {
    this.showBannerCropper = false;
    this.bannerFileForCropping = null;
    this.bannerError = null;
    this.bannerFile = event.croppedFile;
    const reader = new FileReader();
    reader.onload = (e: any) => {
      this.bannerPreview = e.target.result;
    };
    reader.readAsDataURL(event.croppedFile);
  }

  onBannerCropCancel() {
    this.showBannerCropper = false;
    this.bannerFileForCropping = null;
  }

  uploadLogo() {
    if (!this.logoFile) return;
    this.logoUploadLoading = true;
    this.companyService.uploadCompanyLogo(this.logoFile).subscribe({
      next: (response) => {
        this.currentLogoPath = response.file_path;
        this.logoFile = null;
        this.logoPreview = null;
        this.logoError = null;
        this.logoUploadLoading = false;
      },
      error: (error) => {
        this.logger.error('Error uploading logo:', error);
        this.logoUploadLoading = false;
        this.logoError = error.error?.detail || 'Fehler beim Hochladen des Logos.';
      },
    });
  }

  uploadBanner() {
    if (!this.bannerFile) return;
    this.bannerUploadLoading = true;
    this.companyService.uploadCompanyBanner(this.bannerFile).subscribe({
      next: (response) => {
        this.currentBannerPath = response.file_path;
        this.bannerFile = null;
        this.bannerPreview = null;
        this.bannerError = null;
        this.bannerUploadLoading = false;
      },
      error: (error) => {
        this.logger.error('Error uploading banner:', error);
        this.bannerUploadLoading = false;
        this.bannerError = error.error?.detail || 'Fehler beim Hochladen des Banners.';
      },
    });
  }

  deleteLogo() {
    this.companyService.deleteCompanyImage('logo').subscribe({
      next: () => {
        this.currentLogoPath = null;
      },
      error: (error) => {
        this.logger.error('Error deleting logo:', error);
        this.logoError = error.error?.detail || 'Fehler beim Löschen des Logos.';
      },
    });
  }

  deleteBanner() {
    this.companyService.deleteCompanyImage('banner').subscribe({
      next: () => {
        this.currentBannerPath = null;
      },
      error: (error) => {
        this.logger.error('Error deleting banner:', error);
        this.bannerError = error.error?.detail || 'Fehler beim Löschen des Banners.';
      },
    });
  }

  getImageUrl(imagePath: string): string {
    if (!imagePath) return '';
    const cleanPath = imagePath.startsWith('uploads/') ? imagePath.substring(8) : imagePath;
    return `${this.companyService.getApiBaseUrl()}/images/${cleanPath}`;
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
